<template>
  <div class="relative">
    <LineChartGenerator
      data-testid="simple-line-chart"
      :options="options"
      :data="chartData"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
    <BenchmarkLogoBlack
      class="absolute pointer-events-none z-[5] top-[25%] lg:top-[18%] left-1/2 -translate-x-[40%] w-[65%] h-auto opacity-10"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Line as LineChartGenerator } from 'vue-chartjs'
import resolveConfig from 'tailwindcss/resolveConfig'
import { numberFormatter } from '@/utils/number'
import { reformat } from '~/utils/date'
import { logError } from '~/utils/log-error'
import tailwindConfig from '~/tailwind.config'
import BenchmarkLogoBlack from '~/assets/icons/BenchmarkLogoBlack.svg'

const fullConfig = resolveConfig(tailwindConfig)

const props = withDefaults(
  defineProps<{
    chartData?: Record<string, any>
    width?: number
    height?: number
    cssClasses?: string
    styles?: Record<string, any>
    plugins?: Array<any>
    yAxisLabel: string
    isMobile: boolean
  }>(),
  {
    chartData: () => ({}),
    width: 400,
    height: 400,
    cssClasses: '',
    styles: () => ({}),
    plugins: () => [],
  },
)

const options = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      tension: 0, // disables bezier curves
    },
    point: {
      radius: 0,
    },
  },
  scales: {
    y: {
      display: true,
      position: 'left',
      ticks: {
        font: {
          family: 'Montserrat',
          size: props.isMobile ? 8 : 14,
          color: fullConfig.theme.colors.s['900'],
          weight: '600',
        },
        padding: props.isMobile ? 20 : 14,
        beginAtZero: true,
        callback(value: number) {
          return value > 0 ? `${numberFormatter(value)}` : value
        },
      },
      title: {
        display: true,
        text: props.yAxisLabel,
        font: {
          family: 'Montserrat',
          color: fullConfig.theme.colors.s['900'],
          size: props.isMobile ? 8 : 14,
          weight: '600',
        },
      },
    },
    x: {
      type: 'time',
      time: {
        unit: 'month',
      },
      bounds: 'ticks',
      ticks: {
        font: {
          family: 'Montserrat',
          size: props.isMobile ? 8 : 14,
          color: fullConfig.theme.colors.s['900'],
          weight: '600',
        },
        padding: 14,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
  plugins: {
    crosshair: {
      line: {
        color: fullConfig.theme.colors.s['500'],
        width: 1,
      },
      zoom: {
        enabled: false,
      },
      snap: {
        enabled: true,
      },
      sync: {
        enabled: false,
      },
    },
    legend: {
      display: true,
      position: 'bottom',
      align: 'start',
      labels: {
        font: {
          family: 'lato',
          size: 10,
          color: fullConfig.theme.colors.s['900'],
          weight: '600',
        },
        boxWidth: 12,
        padding: props.isMobile ? 17 : 23,
        fillStyle: 'transparent',
      },
    },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      titleFont: {
        family: 'Montserrat',
        size: 14,
      },
      bodyFont: {
        family: 'Montserrat',
        size: 12,
      },
      multiKeyBackground: '#00000000',
      bodySpacing: 8,
      padding: {
        x: 12,
        y: 8,
      },
      callbacks: {
        label(context: any) {
          const { dataIndex, dataset } = context
          const { label, digits } = context.dataset
          const labelReplaceFirst = label.replace(/[*]+/g, '').trim()
          return `${labelReplaceFirst}: ${numberFormatter(
            dataset.data[dataIndex].y,
            digits,
          )}`
        },
        labelColor(context: any) {
          return {
            backgroundColor: context.dataset.borderColor,
          }
        },
        title(tooltipItems: any) {
          const { dataIndex } = tooltipItems[0]
          const tooltipItem = tooltipItems[0].dataset.data[dataIndex]
          const date = tooltipItem.x

          try {
            return reformat(date, 'MMM dd, yyyy')
          } catch (e) {
            logError(e)
          }
        },
      },
    },
  },
}))
</script>
